import { useEffect, useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, CheckCheck, Clock, Receipt, Car, ShieldAlert, FileText, Users } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ICONS = {
  timesheet: Clock,
  invoice: Receipt,
  kilometre: Car,
  incident: ShieldAlert,
  vital: FileText,
  worker: Users,
  default: FileText,
};

export default function ApprovalReviewDialog({ open, onOpenChange, onApproved }) {
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/notifications/pending-summary");
        if (cancelled) return;
        setSummary(res.data);
        // Pre-select everything that's NOT an outlier; outliers default unchecked so they
        // get a deliberate eyes-on review.
        setSelected(new Set(res.data.items.filter((i) => !i.outlier).map((i) => i.id)));
      } catch (e) {
        toast.error(formatApiError(e));
        onOpenChange?.(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, onOpenChange]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectedTotals = useMemo(() => {
    if (!summary) return { count: 0, money: 0 };
    let money = 0;
    summary.items.forEach((i) => { if (selected.has(i.id) && i.amount) money += i.amount; });
    return { count: selected.size, money: Math.round(money * 100) / 100 };
  }, [selected, summary]);

  const selectAll = () => setSelected(new Set(summary.items.map((i) => i.id)));
  const selectSafe = () => setSelected(new Set(summary.items.filter((i) => !i.outlier).map((i) => i.id)));
  const clearAll = () => setSelected(new Set());

  const submit = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const res = await api.post("/notifications/approve-all", { ids: Array.from(selected) });
      const { approved = 0, errors = [] } = res.data || {};
      if (approved > 0 && errors.length === 0) toast.success(`Approved ${approved} request${approved === 1 ? "" : "s"}`);
      else if (approved > 0) toast.success(`Approved ${approved}, ${errors.length} skipped`);
      else toast.error("Nothing approved");
      onOpenChange?.(false);
      onApproved?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-2xl max-h-[90vh] flex flex-col" data-testid="approval-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <CheckCheck className="w-5 h-5" /> Review &amp; approve
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Untick any you want to skip — they'll stay pending for individual review. Outliers (long shifts, big invoices, long trips) are unticked by default.
          </DialogDescription>
        </DialogHeader>

        {!summary ? (
          <div className="py-8 text-center text-muted-foreground" data-testid="approval-review-loading">Loading…</div>
        ) : summary.count === 0 ? (
          <div className="py-8 text-center text-muted-foreground" data-testid="approval-review-empty">
            Nothing pending to review.
          </div>
        ) : (
          <>
            {/* Headline stats */}
            <div className="grid grid-cols-3 gap-3 px-1">
              <Stat label="Pending" value={summary.count} />
              <Stat label="Total $" value={`$${summary.total_money.toLocaleString("en-AU", { minimumFractionDigits: 2 })}`} />
              <Stat label="Outliers" value={summary.outliers} accent={summary.outliers > 0 ? "amber" : null} />
            </div>

            {/* Quick filters */}
            <div className="flex flex-wrap gap-2 px-1" data-testid="approval-review-toolbar">
              <button onClick={selectAll} data-testid="select-all-btn"
                      className="text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border-2 hover:bg-muted">
                Select all
              </button>
              <button onClick={selectSafe} data-testid="select-safe-btn"
                      className="text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border-2 hover:bg-muted">
                Skip outliers
              </button>
              <button onClick={clearAll} data-testid="select-none-btn"
                      className="text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 border-2 hover:bg-muted">
                Clear
              </button>
              <div className="flex-1" />
              <div className="text-xs font-bold text-muted-foreground self-center" data-testid="selected-count">
                {selectedTotals.count} of {summary.count} selected
              </div>
            </div>

            {/* List */}
            <div className="overflow-y-auto rounded-2xl border-2 divide-y" data-testid="approval-review-list">
              {summary.items.map((item) => {
                const Icon = ICONS[item.entity_type] || ICONS.default;
                const checked = selected.has(item.id);
                return (
                  <label
                    key={item.id}
                    htmlFor={`row-${item.id}`}
                    data-testid={`review-row-${item.id}`}
                    className={`flex items-start gap-3 p-3 cursor-pointer transition ${checked ? "bg-client/5" : ""} ${item.outlier ? "border-l-4 border-amber-500" : ""}`}
                  >
                    <Checkbox id={`row-${item.id}`} checked={checked}
                              onCheckedChange={() => toggle(item.id)}
                              data-testid={`review-checkbox-${item.id}`}
                              className="mt-1" />
                    <Icon className="w-4 h-4 mt-1 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-bold text-sm capitalize">{item.entity_type}</span>
                        <Badge variant="outline" className="text-[10px] uppercase font-bold tracking-wider">
                          {item.action}
                        </Badge>
                        {item.outlier && (
                          <Badge variant="outline" className="text-[10px] uppercase font-bold tracking-wider border-amber-500 text-amber-700">
                            <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Outlier
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground mt-0.5 truncate">
                        {item.summary || `${item.actor_name || "Someone"} submitted a change`}
                      </div>
                      {item.outlier_reason && (
                        <div className="text-xs text-amber-700 dark:text-amber-300 mt-1 font-semibold flex items-start gap-1"
                             data-testid={`outlier-reason-${item.id}`}>
                          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                          {item.outlier_reason}
                        </div>
                      )}
                    </div>
                    {item.amount !== null && item.amount !== undefined && (
                      <div className="font-mono font-bold text-sm whitespace-nowrap shrink-0">
                        ${item.amount.toLocaleString("en-AU", { minimumFractionDigits: 2 })}
                      </div>
                    )}
                  </label>
                );
              })}
            </div>
          </>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-full font-bold"
                  data-testid="review-cancel-btn">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy || selected.size === 0}
                  className="rounded-full font-bold gap-2 min-w-[180px]"
                  style={{ background: "hsl(var(--client))" }}
                  data-testid="review-submit-btn">
            <CheckCheck className="w-4 h-4" />
            {busy ? "Approving…" :
             selectedTotals.count === 0 ? "Pick at least one" :
             selectedTotals.money > 0 ? `Approve ${selectedTotals.count} · $${selectedTotals.money.toLocaleString("en-AU", { minimumFractionDigits: 2 })}` :
             `Approve ${selectedTotals.count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl border-2 p-3 text-center">
      <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground">{label}</div>
      <div className={`font-display font-black text-2xl ${accent === "amber" ? "text-amber-600" : ""}`}>{value}</div>
    </div>
  );
}
